import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WalletContext, type WalletContextValue } from '@/app/providers/wallet';
import { useCurrentUser } from '@/app/providers/user';
import { getBalance, getPaymentMethod } from '@/features/wallet/services/walletService';
import { safeErrorMessage } from '@/shared/utils/errors';
import type { PaymentMethodSummary, WalletBalance } from '@/shared/types';

// Tracks the signed-in user's wallet balance and saved card by reading the real
// /api/v1/credits/* proxy through walletService. Loads on mount / user change and
// exposes refreshers the WalletPage calls after a Stripe confirmation settles
// (card save and top-up crediting are webhook-driven, so the page polls these).
export function WalletProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const userId = user?.userId;
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against a stale in-flight load (user switched) clobbering fresh state.
  const loadIdRef = useRef(0);

  const refreshBalance = useCallback(async (): Promise<WalletBalance | null> => {
    if (!userId) return null;
    const next = await getBalance();
    setBalance(next);
    return next;
  }, [userId]);

  const refreshPaymentMethod = useCallback(async (): Promise<PaymentMethodSummary | null> => {
    if (!userId) return null;
    const next = await getPaymentMethod();
    setPaymentMethod(next);
    return next;
  }, [userId]);

  const load = useCallback(async (): Promise<void> => {
    // Unauthenticated -> empty wallet, no fetch.
    if (!userId) {
      setBalance(null);
      setPaymentMethod(null);
      setLoading(false);
      setError(null);
      return;
    }

    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);

    // allSettled (not all): if only one call fails we still render the other's
    // data — e.g. a card-read hiccup shouldn't blank out a balance that loaded fine.
    const [balanceResult, paymentMethodResult] = await Promise.allSettled([
      getBalance(),
      getPaymentMethod(),
    ]);
    if (loadId !== loadIdRef.current) return; // superseded by a newer load

    if (balanceResult.status === 'fulfilled') setBalance(balanceResult.value);
    if (paymentMethodResult.status === 'fulfilled') setPaymentMethod(paymentMethodResult.value);

    const failure =
      balanceResult.status === 'rejected'
        ? balanceResult.reason
        : paymentMethodResult.status === 'rejected'
          ? paymentMethodResult.reason
          : null;
    if (failure) {
      setError(safeErrorMessage(failure, 'Unable to load your wallet.'));
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<WalletContextValue>(
    () => ({ balance, paymentMethod, loading, error, refreshBalance, refreshPaymentMethod, reload: load }),
    [balance, paymentMethod, loading, error, refreshBalance, refreshPaymentMethod, load],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
